import { Map } from "immutable";
import { mat2d, vec2 } from "gl-matrix";

import { getRadiansFromAngle, rotateMatrixAroundPoint } from "./math/Angles";
import DataTypes from "@/constants/DataTypes";
import SiteDesignStore from "@/stores/SiteDesignStore";
import { getTargetBounds } from "./Transformer/targets";
import { calculateBounds, updateToolMatrix } from "./Transformer/tool";
import { Positions } from "@/constants/TransformerConstants";
import { scaleAxes } from "./Transformer/handlers";
import { getScaleTranslation } from "./Transformer/translation";
import { gatherDimensionPositions } from "./Element/SizeAndPositionUtils";
import IDUtils from "./IDUtils";

import { ELEMENTS } from "@/constants/EntityConstants";
import { TRANSITION_IN } from "@/constants/AnimationConstants";
import { getProp } from "./Merge/MergeUtils.mjs";

/**
 * @typedef {import("@/types/ElementTypes.mjs").DimensionPosition} DimensionPosition
 * @typedef {import("@/types/TransformerTypes.mjs").ElementInfo} ElementInfo
 * @typedef {import("@/types/TransformerTypes.mjs").TransformDiff} TransformDiff
 * @typedef {import("@/types/Selection.mjs").ElementInfo} ElementSelectionInfo
 * @typedef {typeof Positions.TOP_LEFT | typeof Positions.TOP_RIGHT | typeof Positions.BOTTOM_LEFT | typeof Positions.BOTTOM_RIGHT } CornerNames
 * @typedef {import("@/vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 * @typedef {import("@/types/ElementTypes.mjs").ImmutableElement} ImmutableElement
 * @typedef {import("@/types/ElementTypes.mjs").ImmutableContainer} ImmutableContainer
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} Layouts
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 */

// * maths and positioning utils

/**
 * Positions a contained element to design space (global position without stage offsets,
 * as in relative to the design's top left) using the parent's xy and rotation.
 * @param {DimensionPosition} element
 * @param {DimensionPosition} container
 * @param {boolean} withOffsets - include container offsets with resulting PositionDimensions
 * @returns {DimensionPosition}
 */

export const positionElementToContainer = (
  element,
  container,
  withOffsets = false,
) => {
  const elementCenter = [element.w / 2 + element.x, element.h / 2 + element.y];

  // Never-rotated elements store no angle
  const containerAngle = container.a ?? 0;
  const containerMat = mat2d.create();
  const containerRads = getRadiansFromAngle(containerAngle);

  // rotate container tl around it's center
  rotateMatrixAroundPoint(
    containerMat,
    container.w / 2,
    container.h / 2,
    containerRads,
  );

  // rotate to container
  const translatedTL = vec2.transformMat2d(
    vec2.create(),
    elementCenter,
    containerMat,
  );
  // remove center diff
  let [x, y] = vec2.sub(vec2.create(), translatedTL, [
    element.w / 2,
    element.h / 2,
  ]);

  if (withOffsets) {
    // shift with container xy
    [x, y] = vec2.add(vec2.create(), [x, y], [container.x, container.y]);
  }

  return {
    ...element,
    a: (withOffsets ? containerAngle : 0) + (element.a ?? 0),
    x: x,
    y: y,
  };
};

/**
 * Translates an element's coordinates to it's container space.
 * Effectively removes container's offsets from the element.
 * @param {DimensionPosition} element
 * @param {DimensionPosition} container
 * @returns {DimensionPosition}
 */
export const translateElementToContainerSpace = (element, container) => {
  const elementCenter = [element.w / 2 + element.x, element.h / 2 + element.y];
  const containerCenter = [
    container.w / 2 + container.x,
    container.h / 2 + container.y,
  ];

  const containerMat = mat2d.create();
  const containerRads = getRadiansFromAngle(-(container.a ?? 0));
  const sizeDiff = [
    (container.w - element.w) / 2,
    (container.h - element.h) / 2,
  ];

  rotateMatrixAroundPoint(containerMat, 0, 0, containerRads);

  // remove offsets so container center is 0,0
  const elementWithoutOffset = vec2.sub(
    vec2.create(),
    elementCenter,
    containerCenter,
  );
  // rotate element center without offset
  const rotatedOffset = vec2.transformMat2d(
    vec2.create(),
    elementWithoutOffset,
    containerMat,
  );
  // add size diff back
  const [x, y] = vec2.add(vec2.create(), rotatedOffset, sizeDiff);

  return {
    ...element,
    a: (element.a ?? 0) - (container.a ?? 0),
    x: x,
    y: y,
  };
};

/**
 * Gets the opposite corner name
 * @param {CornerNames} cornerName
 * @returns {CornerNames}
 */
const getOppositeCornerName = (cornerName) =>
  ({
    [Positions.TOP_LEFT]: Positions.BOTTOM_RIGHT,
    [Positions.TOP_RIGHT]: Positions.BOTTOM_LEFT,
    [Positions.BOTTOM_RIGHT]: Positions.TOP_LEFT,
    [Positions.BOTTOM_LEFT]: Positions.TOP_RIGHT,
  })[cornerName];

/**
 * Returns the x/y for a corner of a position. Can add offsets to shift.
 * @param {CornerNames} corner
 * @param {DimensionPosition} position
 * @param {{ x: number, y: number}=} offsets
 * @returns {Array<number>}
 */
const getCornerValue = (corner, position, offsets = { x: 0, y: 0 }) =>
  ({
    [Positions.TOP_LEFT]: () => [offsets.x + 0, offsets.y + 0],
    [Positions.TOP_RIGHT]: () => [offsets.x + position.w, offsets.y + 0],
    [Positions.BOTTOM_RIGHT]: () => [
      offsets.x + position.w,
      position.h + offsets.y,
    ],
    [Positions.BOTTOM_LEFT]: () => [offsets.x + 0, position.h + offsets.y],
  })[corner]();

// todo: figure out if this is still necessary
/**
 * Effectively tries to find the corner of change, to simulate dragging a corner.
 * @param {DimensionPosition} scaled - Scaled shape
 * @param {DimensionPosition} base - Base shape
 * @returns {{ corner: string, diff: number }} - Corner with the biggest offset and it's diff
 */
const findOverageCorner = (scaled, base) => {
  const positionDiff = vec2.subtract(
    vec2.create(),
    [scaled.x, scaled.y],
    [base.x, base.y],
  );

  const scaledCorners = {
    [Positions.TOP_LEFT]: [0 + positionDiff[0], 0 + positionDiff[1]],
    [Positions.BOTTOM_RIGHT]: [
      scaled.w + positionDiff[0],
      scaled.h + positionDiff[1],
    ],
    [Positions.TOP_RIGHT]: [scaled.w + positionDiff[0], 0 + positionDiff[1]],
    [Positions.BOTTOM_LEFT]: [0 + positionDiff[0], scaled.h + positionDiff[1]],
  };

  return Object.entries(scaledCorners).reduce(
    (result, [corner, value]) => {
      const baseCorner = getCornerValue(corner, base);
      const distance = vec2.distance(baseCorner, value);
      // return distance > result.diff || !result.corner ? { corner, diff: distance } : result;
      return corner === Positions.TOP_LEFT
        ? { corner, diff: distance }
        : result;
    },
    { corner: "", diff: 0 },
  );
};

/**
 * Effectively simulate dragging the tl corner for scaling purposes.
 * @param {DimensionPosition} scaled - Scaled shape
 * @param {DimensionPosition} base - Base shape
 * @returns {{ corner: string, diff: number }} - Corner with the biggest offset and it's diff
 */
const findTLOverage = (scaled, base) => {
  const positionDiff = vec2.subtract(
    vec2.create(),
    [scaled.x, scaled.y],
    [base.x, base.y],
  );
  const scaledTL = [0 + positionDiff[0], 0 + positionDiff[1]];
  const baseTL = getCornerValue(Positions.TOP_LEFT, base);
  return { corner: Positions.TOP_LEFT, diff: vec2.distance(baseTL, scaledTL) };
};

/**
 * Moves the container to account for resized container.
 * @param {DimensionPosition} scaled - Scaled shape
 * @param {DimensionPosition} base - Base shape
 * @return {vec2} - xyOffset
 */
const moveScaledContainer = (scaled, base, cornerDiff) => {
  const oppositeCorner = getOppositeCornerName(cornerDiff.corner);

  const positionDiff = vec2.subtract(
    vec2.create(),
    [scaled.x, scaled.y],
    [base.x, base.y],
  );

  const shapeMatrix = updateToolMatrix(base);
  const regPoint = getCornerValue(oppositeCorner, base);
  const controlPoint = getCornerValue(cornerDiff.corner, base);
  const regOffset = vec2.subtract(vec2.create(), controlPoint, regPoint);

  const scaledAxes = scaleAxes(regOffset, positionDiff);

  return getScaleTranslation(regPoint, shapeMatrix, scaledAxes, {
    ...scaled,
    a: base.a,
  }).xyOffset;
};

/**
 * Resizes element container when elements move within it (or are added or removed)
 * @param {Record<string, DimensionPosition>} positions
 * @param {DimensionPosition} container
 * @returns {[DimensionPosition, Record<string, DimensionPosition>]} - the resized container, and a record of updated element dimensions with their id as key.
 */
export const resizeContainer = (positions, container) => {
  const targets = Object.entries(positions).reduce((result, [id, position]) => {
    result[id] = {
      ...position,
      bounds: getTargetBounds({ ...position }, container),
    };
    return result;
  }, {});

  const resizedContainer = calculateBounds(targets, {}, { top: 0, left: 0 });

  const prevCoordinates = [container.x, container.y];
  const newCoordinates = [resizedContainer.x, resizedContainer.y];
  const containerShift = vec2.subtract(
    vec2.create(),
    prevCoordinates,
    newCoordinates,
  );

  // apply container shift to elements to make them appear as though they haven't moved
  const shiftedTargets = Object.entries(targets).reduce(
    (result, [id, data]) => {
      const { bounds, ...rest } = data;
      result[id] = {
        ...rest,
        x: rest.x + containerShift[0],
        y: rest.y + containerShift[1],
      };
      return result;
    },
    {},
  );

  // hold on to this for now 👇
  // const overageCorner = findOverageCorner(resizedContainer, container);
  const overageCorner = findTLOverage(resizedContainer, container);
  const scaledXY = moveScaledContainer(
    resizedContainer,
    container,
    overageCorner,
  );

  return [
    {
      ...resizedContainer,
      x: scaledXY[0],
      y: scaledXY[1],
      a: container.a,
    },
    shiftedTargets,
  ];
};

/**
 * Redirects an xy move to a container's angle, effectively unrotating the vector. Invert the angle argument to do the opposite.
 * @param {Coordinates} coordinates
 * @param {number} baseAngle
 * @returns {Coordinates}
 */
export const redirectMove = ([xDiff, yDiff], baseAngle) => {
  const containerRads = getRadiansFromAngle(-baseAngle);

  // rotates diff relative to container rotation
  return vec2.transformMat2d(
    vec2.create(),
    [xDiff, yDiff],
    mat2d.fromRotation(mat2d.create(), containerRads),
  );
};

// * actions

/**
 * Checks selection for grouping eligibility.
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @returns {boolean}
 */
export const canSelectionGroup = (selectedElements) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  return (
    editableElements.every(
      (element) => !element.containedBy && element.type !== DataTypes.GROUP,
    ) && editableElements.size > 1
  );
};

/**
 * Checks selection for grouping dissolution.
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @returns {boolean}
 */
export const canSelectedGroupDissolve = (selectedElements) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  return (
    editableElements.size === 1 &&
    editableElements.first().type === DataTypes.GROUP
  );
};

/**
 * Checks selection for group join eligibility. The group must be in the same selection for this.
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @returns {boolean}
 */
export const canSelectionJoinGroup = (selectedElements) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  const hasOneGroup =
    editableElements.filter((element) => element.type === DataTypes.GROUP)
      .size === 1;

  if (!hasOneGroup || editableElements.size < 2) return false;

  const group = selectedElements.find(
    (element) => element.type === DataTypes.GROUP,
  );
  const block = SiteDesignStore.getBlock(group.blockId);
  const containerData = block.getIn(IDUtils.getArray(group.id, 1));

  if (!containerData) return false;

  return containerData.get("lockEditing") !== "1";
};

/**
 * hecks selection for group removal eligibility.
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @returns {boolean}
 */
export const canRemoveFromGroup = (selectedElements) => {
  if (!selectedElements.size) return;

  const editableElements = selectedElements.filter((element) => !element?.lock);
  const allContained = editableElements.every((element) => element.contained);
  const groupBlockId = selectedElements.first().blockId;

  const block = SiteDesignStore.getBlock(groupBlockId);
  const containerData = getContainerData(block, selectedElements.first().id);

  if (containerData ? containerData.get("lockEditing") === "1" : true)
    return false;
  if (!allContained || !editableElements.size) return false;
  if (allContained && editableElements.size === 1) return true;

  const sameGroup = editableElements
    .slice(1)
    .reduce(
      (result, element) =>
        result.value === false || element.containedBy !== result.containerId
          ? { ...result, value: false }
          : result,
      { value: true, containerId: editableElements.first().containedBy },
    );

  return sameGroup.value;
};

/**
 * Creates a group from a selection
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @param {Map} block
 */
export const groupSelection = (selectedElements, block) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  SiteDesignStore.groupSelection(editableElements, block.get("id"));
};

/**
 * Creates a group from a selection
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @param {Map} block
 */
export const dissolveSelection = (selectedElements, block) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  SiteDesignStore.dissolveGroup(editableElements.first(), block.get("id"));
};

/**
 * Adds the selection to the group in
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @param {Map} block
 */
export const addSelectionToGroup = (selectedElements, block) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  const targetGroup = editableElements.find(
    (element) => element.type === DataTypes.GROUP,
  );
  const elementsToAdd = editableElements
    .filter((element) => element.type !== DataTypes.GROUP)
    .toArray();
  SiteDesignStore.addToGroup(targetGroup, elementsToAdd, block.get("id"));
};

/**
 * Removes elements from selected group. Places them just above for now probably.
 * @param {Map<string, ElementSelectionInfo>} selectedElements
 * @param {Map} block
 */
export const removeSelectionFromGroup = (selectedElements, block) => {
  const editableElements = selectedElements.filter((element) => !element?.lock);
  SiteDesignStore.removeFromGroup(editableElements.toArray(), block.get("id"));
};

// * data prep

/**
 * Gathers dimension positions for unchanged elements. Used during the group resize process.
 * @param {ImmutableContainer} containerData
 * @param {string} containerId
 * @param {Array<string>} changedElementIds
 * @param {Layouts[number]} layout
 * @returns {ReturnType<gatherDimensionPositions>}
 */
export const gatherUnchangedGroupPositions = (
  containerData,
  containerId,
  changedElementIds,
  layout,
) => {
  const unchangedElements = containerData
    .get("elementOrder")
    .reduce(
      (result, id) =>
        !changedElementIds.includes(id)
          ? result.set(
              `${containerId}.elements.${id}`,
              containerData.getIn(["elements", id, layout]).toJS(),
            )
          : result,
      Map(),
    );

  return gatherDimensionPositions(unchangedElements, true);
};

/**
 *
 * @param {Map} block - Block data, could be another map like selected that follows the same path structure.
 * @param {Array<string> | string} path
 * @returns {Map | undefined}
 */
export const getContainerData = (block, path) => {
  if (!block || !path) return;

  const containerPath = IDUtils.getElementContainerFromPath(
    IDUtils.getArray(path),
  );
  return containerPath ? block.getIn(containerPath.slice(1)) : undefined;
};

// * properties panel utils

/**
 * Check if an element's parent has an action or link set
 * @param {Object} props
 * @param {ImmutableElement=} props.element
 * @param {import("immutable").Map=} props.block
 * @param {ImmutableElement=} props.parent
 *
 * @returns {boolean}
 */
export const parentHasAction = ({ element, block, parent }) => {
  let elementContainer = parent;

  if (!elementContainer && element && element.get("pId")) {
    elementContainer = getContainerData(
      block,
      element.getIn(["selected", "id"]),
    );
  }

  return (
    !!elementContainer &&
    ((elementContainer.has("link") &&
      !!elementContainer?.get?.("link")?.size) ||
      (elementContainer.has("pubs") && !!elementContainer?.get?.("pubs")?.size))
  );
};

/**
 * Generates a record with the key being the contained element path, and a list of propUpdates if link or pubs need to be deleted.
 * @param {object} container
 * @param {string} path
 * @returns {Record<string, Array<PropUpdate>>}
 */
export const generateCleanActionUpdates = (container, path) => {
  return container.get("elements").reduce((updates, data, key) => {
    let update = [];

    if (data.has("link")) update.push({ name: "link", action: "delete" });
    if (data.has("pubs")) update.push({ name: "pubs", action: "delete" });

    if (update.length) {
      updates[`${path}.${ELEMENTS}.${key}`] = update;
    }

    return updates;
  }, {});
};
